import React, {Fragment} from 'react';
import {connect} from "react-redux";
import autobind from 'autobind-decorator';

import {Conditional} from "../Conditional";
import {BaseComponent} from "../BaseComponent";

import {service as ConfirmationStatusService} from  "../../Services/ConfirmationStatusService";
import {service as ContextNodeService} from  "../../Services/ContextNodeService";
import {service as EnvironmentalBenefitService} from "../../Services/EnvironmentalBenefitService";

import {EnvironmentalBenefitFilter} from "./EnvironmentalBenefitFilter";
import {ContextNodeBenefitSummary} from "./ContextNodeBenefitSummary";
import {StreamBankErrorDispatchEnhance} from "./Enhancements";

@StreamBankErrorDispatchEnhance
class _ContextNodeBenefitSummarySection extends BaseComponent {

    constructor(props) {
        super(props);

        this.setInitialState({
            benefitSummary: null,
            confirmationStatuses: [],
            environmentalBenefits: [],
            selectedEnvironmentalBenefitId: null,
        });

    }

    @autobind
    shouldReloadData(prevProps, prevState) {
        return this.props?.contextNodeId != prevProps?.contextNodeId ||

            // TODO: we should do something like this below, but it requires
            // adding additional data to all of the ViewPages and should probably
            // wait for a refactor of those pages to include ContextNodeDetails in
            // a common state pattern
            //this.props.overallConfirmationStatusId != this.prevProps.overallConfirmationStatusId ||
            // in the interim this is a hack to make sure we reload our benefits if
            // the parent page changes:
            this.props?.editMode != prevProps?.editMode;
    }

    @autobind
    loadData() {
        const { contextNodeId, editMode, ErrorDispatch } = this.props;

        super.loadData();

        if(contextNodeId == null || editMode) {
            this.setState({
                benefitSummary: null
            });
        } else {
            Promise
                .all([
                    ContextNodeService.GetBenefitsSummary(contextNodeId),
                    ConfirmationStatusService.Get(),
                    EnvironmentalBenefitService.Get()
                ])
                .then( ([benefitSummary, confirmationStatuses, environmentalBenefits]) => {
                    this.setState({
                        benefitSummary: benefitSummary?.result,
                        confirmationStatuses: confirmationStatuses.result,
                        environmentalBenefits: environmentalBenefits.result,
                        selectedEnvironmentalBenefitId: this.state.selectedEnvironmentalBenefitId ?? environmentalBenefits?.result?.[0]?.environmentalBenefitId
                    });
                })
                .catch((x) => {
                    ErrorDispatch.SetError({
                        Code: x.xhr.status,
                        Message: x.message,
                        Title: x.xhr.statusText,
                        StackTrace: x.data.stackTrace
                    });
                });
        }
    }

    @autobind
    selectedEnvironmentalBenefitIdChanged(selectedEnvironmentalBenefitId) {
        this.setState({
            selectedEnvironmentalBenefitId: selectedEnvironmentalBenefitId
        });
    }

    @autobind
    render() {

        const { contextNodeId, editMode } = this.props;
        const { benefitSummary, confirmationStatuses, environmentalBenefits, selectedEnvironmentalBenefitId } = this.state;

        if(editMode) {
            return null;
        }

        // for public users, only show confirmed data
        const filteredConfirmationStatuses = confirmationStatuses.filter((confirmationStatus) => {
            if (this.props.publicContext) {
                return confirmationStatus?.code === 'confirmed';
            }

            return confirmationStatus?.code !== 'on-hold';
        });

        // only show benefits for which we have modeled uplift
        const filteredEnvironmentalBenefits = environmentalBenefits.filter((environmentalBenefit) => {
            return benefitSummary?.find((contextNode) => {
                return contextNode.environmentalBenefitId == environmentalBenefit.environmentalBenefitId
                    && filteredConfirmationStatuses.some((confirmationStatus) => {
                        return String(confirmationStatus.confirmationStatusId) === String(contextNode.confirmationStatusId)
                    }) 
            });
        });

        // ensure our selected benefit is a valid selection, otherwise use the first available
        let environmentalBenefitId = selectedEnvironmentalBenefitId || "";
        if(environmentalBenefitId !== "" && !filteredEnvironmentalBenefits.find((x) => x.environmentalBenefitId == environmentalBenefitId)) {
            environmentalBenefitId = filteredEnvironmentalBenefits[0]?.environmentalBenefitId ?? "";
        }

        // TODO: SB-1606
        // the goal is to eventually branch this logic here depending on if the ContextNode
        // belongs to a program with one or more on-hold projects so we can show it only
        // for programs that use the on-hold status. However, we don't have an easy way to
        // access this data in this context right now, and the on-hold status is not yet used
        // in any programs, so let's just hide it for all programs until later.

        return <Fragment>
            <h3 className="table-headers">Benefit Summary</h3>

            <Conditional Condition={filteredEnvironmentalBenefits?.length > 0}>
                <EnvironmentalBenefitFilter
                    EnvironmentalBenefits={filteredEnvironmentalBenefits}
                    Value={environmentalBenefitId}
                    UpdateValue={this.selectedEnvironmentalBenefitIdChanged}
                    HideUnselectedOption={true}
                />
            </Conditional>
            <ContextNodeBenefitSummary
                parentContextNodeId={contextNodeId}
                benefitSummary={benefitSummary}
                environmentalBenefitId={environmentalBenefitId}
                confirmationStatuses={filteredConfirmationStatuses}
            />
        </Fragment>

        return null;
    }

}

function mapStateToProps(state) {
    const {userState} = state;
    return {
        publicContext: userState.user?.publicContext,
    }
}

export const ContextNodeBenefitSummarySection = connect(mapStateToProps)(_ContextNodeBenefitSummarySection);
